import errorMessages from '@common/constants/errorMessages';
import {
  ONE_TIME_PAYMENT_MIN_AMOUNT,
  ONE_TIME_PAYMENT_NOTE_MAX_LENGTH,
} from '@rental-manager/constants/PaymentConstants';
import { centsToDollars, dollarsToCents } from '@common/paymentsUtils/currencyUtils';
import { isEmpty, isObjectEmpty } from '@common/paymentsUtils/inputValidator';
import { logError } from '@zg-rentals/log-error';

export const isOneTimePaymentNoteLengthValid = (value: string) => value.length <= ONE_TIME_PAYMENT_NOTE_MAX_LENGTH;

export const isOneTimePaymentAmountValid = (amount: string, maxAmount: number) => {
  const amountInCents = parseInt(dollarsToCents(amount), 10);
  return amountInCents <= maxAmount;
};

export const isOneTimePaymentMinAmountValid = (amount: string, minAmount: number) => {
  const amountInCents = parseInt(dollarsToCents(amount), 10);
  return amountInCents >= minAmount;
};

const validateOneTimePaymentType = (
  oneTimePaymentType: string,
  oneTimePaymentLimits: Record<string, unknown>,
): Record<string, string> => {
  const errors: any = {};
  if (isEmpty(oneTimePaymentType) || oneTimePaymentType === 'selector') {
    errors.oneTimePaymentType = errorMessages.ONE_TIME_PAYMENT_TYPE_EMPTY;
    return errors;
  }

  if (!oneTimePaymentLimits[oneTimePaymentType]) {
    errors.oneTimePaymentType = errorMessages.ONE_TIME_PAYMENT_TYPE_INVALID;
    logError({
      error: new Error('OTP payment type is invalid.'),
      context: {
        details: oneTimePaymentType,
      },
    });
  }

  return errors;
};

const validateOneTimePaymentAmount = (
  oneTimePaymentAmount: string,
  maxAmountCents: number,
  minAmountCents: number,
): Record<string, string> => {
  const errors: any = {};
  if (isEmpty(oneTimePaymentAmount)) {
    errors.oneTimePaymentAmount = errorMessages.ONE_TIME_PAYMENT_AMOUNT_EMPTY;
    return errors;
  }

  if (!isOneTimePaymentAmountValid(oneTimePaymentAmount, maxAmountCents)) {
    const oneTimePaymentMaxAmountInDollars = centsToDollars(maxAmountCents);
    errors.oneTimePaymentAmount = errorMessages.ONE_TIME_PAYMENT_MAX_AMOUNT(oneTimePaymentMaxAmountInDollars);
  }

  if (!isOneTimePaymentMinAmountValid(oneTimePaymentAmount, minAmountCents)) {
    errors.oneTimePaymentAmount = errorMessages.ONE_TIME_PAYMENT_MIN_AMOUNT;
  }
  return errors;
};

const validateOneTimePaymentNotes = (oneTimePaymentNotes: string): Record<string, string> => {
  const errors: any = {};
  if (isEmpty(oneTimePaymentNotes)) {
    errors.oneTimePaymentNotes = errorMessages.ONE_TIME_PAYMENT_NOTES_EMPTY;
  }
  return errors;
};

export const validateInputs = (ctx) => {
  const { selectedTenantIndex, oneTimePaymentLimitsArray } = ctx.props;
  const { oneTimePaymentType, oneTimePaymentAmount, oneTimePaymentNotes } = ctx.state;
  const oneTimePaymentLimits = oneTimePaymentLimitsArray[selectedTenantIndex];

  const errors = {
    ...validateOneTimePaymentType(oneTimePaymentType, oneTimePaymentLimits),
    ...validateOneTimePaymentAmount(
      oneTimePaymentAmount,
      Number(oneTimePaymentLimits[oneTimePaymentType]),
      ONE_TIME_PAYMENT_MIN_AMOUNT,
    ),
    ...validateOneTimePaymentNotes(oneTimePaymentNotes),
  };
  ctx.setState({ errors });
  return isObjectEmpty(errors);
};
